import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHeader, EmptyState, Section } from "@/components/Shared";
import api, { formatApiError } from "@/lib/api";
import { TL, ACCOUNT_TYPE_LABEL, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Banknote, Wallet, ArrowLeftRight, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

function AccountForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", type: "kasa", bank_name: "", iban: "", opening_balance: "0" });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/hesaplar", {
        name: form.name,
        type: form.type,
        bank_name: form.bank_name,
        iban: form.iban,
        opening_balance: parseFloat(form.opening_balance) || 0,
      });
      toast.success("Hesap oluşturuldu");
      setOpen(false);
      setForm({ name: "", type: "kasa", bank_name: "", iban: "", opening_balance: "0" });
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-account-button">
          <Plus className="w-4 h-4 mr-2" /> Yeni Hesap
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Yeni Kasa / Banka Hesabı</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Hesap Adı</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name-input" placeholder="Örn. Resmi Hesap" />
            </div>
            <div className="space-y-2">
              <Label>Tür</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="kasa">Kasa</SelectItem>
                  <SelectItem value="banka">Banka</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Açılış Bakiye (₺)</Label>
              <Input type="number" step="0.01" value={form.opening_balance}
                     onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} data-testid="account-opening-balance" />
            </div>
            {form.type === "banka" && (
              <>
                <div className="space-y-2">
                  <Label>Banka Adı</Label>
                  <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="account-bank-name" placeholder="Örn. Bankanızın adı" />
                </div>
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="account-iban" placeholder="TR00 0000 0000 0000 0000 0000 00" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="account-submit-button">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TransferForm({ accounts, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ from_account_id: "", to_account_id: "", date: todayISO(), amount: "", description: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/transferler", { ...form, amount: parseFloat(form.amount) });
      toast.success("Transfer başarılı");
      setOpen(false);
      setForm({ from_account_id: "", to_account_id: "", date: todayISO(), amount: "", description: "" });
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="transfer-button">
          <ArrowLeftRight className="w-4 h-4 mr-2" /> Transfer
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Hesaplar Arası Transfer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Kaynak Hesap</Label>
            <Select value={form.from_account_id} onValueChange={(v) => setForm({ ...form, from_account_id: v })}>
              <SelectTrigger data-testid="transfer-from-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({ACCOUNT_TYPE_LABEL[a.type]})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Hedef Hesap</Label>
            <Select value={form.to_account_id} onValueChange={(v) => setForm({ ...form, to_account_id: v })}>
              <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {accounts.filter((a) => a.id !== form.from_account_id).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name} ({ACCOUNT_TYPE_LABEL[a.type]})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tarih</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="transfer-date" />
            </div>
            <div className="space-y-2">
              <Label>Tutar (₺)</Label>
              <Input required type="number" step="0.01" min="0.01" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="transfer-amount" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Açıklama</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="transfer-description" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button type="submit" disabled={saving || !form.from_account_id || !form.to_account_id}
                    className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="transfer-submit-button">
              {saving ? "Aktarılıyor..." : "Transfer Et"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AccountEditDialog({ account, open, onOpenChange, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", bank_name: "", iban: "" });

  useEffect(() => {
    if (account) {
      setForm({
        name: account.name || "",
        bank_name: account.bank_name || "",
        iban: account.iban || "",
      });
    }
  }, [account]);

  const submit = async (e) => {
    e.preventDefault();
    if (!account) return;
    setSaving(true);
    try {
      await api.patch(`/hesaplar/${account.id}`, form);
      toast.success("Hesap güncellendi");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-account-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Hesabı Düzenle</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Hesap Adı</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-account-name-input" />
          </div>
          {account?.type === "banka" && (
            <>
              <div className="space-y-2">
                <Label>Banka Adı</Label>
                <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="edit-account-bank-name" placeholder="Örn. Bankanızın adı" />
              </div>
              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="edit-account-iban" placeholder="TR00 0000 0000 0000 0000 0000 00" />
              </div>
            </>
          )}
          <div className="text-xs text-foreground/50">
            Not: Hesap türü ve açılış bakiyesi düzenlenemez. Değiştirmek isterseniz hesabı silip yeniden oluşturmanız gerekir.
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-edit-account">Vazgeç</Button>
            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="confirm-edit-account">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AccountGrid({ accounts, onChanged }) {
  const [pending, setPending] = useState(null);
  const [editing, setEditing] = useState(null);

  const doDelete = async () => {
    if (!pending) return;
    try {
      await api.delete(`/hesaplar/${pending.id}`);
      toast.success(`"${pending.name}" silindi`);
      setPending(null);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (accounts.length === 0) return null;
  return (
    <>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {accounts.map((a) => (
        <div key={a.id} className="relative border border-border rounded-md p-5 bg-background hover:border-foreground/20 transition-colors group" data-testid={`account-card-${a.id}`}>
          <Link to={`/hesaplar/${a.id}`} className="block" data-testid={`account-link-${a.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                {a.type === "kasa" ? <Banknote className="w-4 h-4 text-foreground/50" strokeWidth={1.5} /> : <Wallet className="w-4 h-4 text-foreground/50" strokeWidth={1.5} />}
                <div className="text-[11px] uppercase tracking-wider text-foreground/50">{ACCOUNT_TYPE_LABEL[a.type]}</div>
              </div>
            </div>
            <div className="font-heading font-medium text-lg mt-3 text-foreground">{a.name}</div>
            {a.bank_name && <div className="text-xs text-foreground/60">{a.bank_name}</div>}
            <div className="font-heading text-2xl font-medium tracking-tight tabular-nums mt-3">{TL(a.balance)}</div>
            {a.blocked_balance > 0 && (
              <div className="text-xs text-amber-700 mt-1 tabular-nums" data-testid={`account-blocked-${a.id}`}>
                Blokede: {TL(a.blocked_balance)}
              </div>
            )}
          </Link>
          <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(a); }}
              className="text-foreground/30 hover:text-primary transition-colors p-1"
              data-testid={`edit-account-${a.id}`}
              aria-label={`${a.name} düzenle`}
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPending(a); }}
              className="text-foreground/30 hover:text-destructive transition-colors p-1"
              data-testid={`delete-account-${a.id}`}
              aria-label={`${a.name} sil`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>

    <AccountEditDialog
      account={editing}
      open={!!editing}
      onOpenChange={(o) => !o && setEditing(null)}
      onSaved={() => { setEditing(null); onChanged?.(); }}
    />

    <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
      <AlertDialogContent data-testid="delete-account-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">Hesap silinsin mi?</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="font-medium text-foreground">{pending?.name}</span> ve buna bağlı tüm hareketler
            kalıcı olarak silinecek. Bu işlem geri alınamaz.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-delete-account">Vazgeç</AlertDialogCancel>
          <AlertDialogAction
            onClick={doDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="confirm-delete-account"
          >
            Sil
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

export default function HesaplarPage() {
  const [accounts, setAccounts] = useState([]);
  const load = async () => {
    const { data } = await api.get("/hesaplar");
    setAccounts(data);
  };
  useEffect(() => { load().catch(console.error); }, []);

  const kasalar = accounts.filter((a) => a.type === "kasa");
  const bankalar = accounts.filter((a) => a.type === "banka");

  return (
    <Layout>
      <PageHeader
        title="Kasa & Banka Hesapları"
        subtitle="Nakit ve banka hesaplarınızı yönetin, transfer yapın"
        action={
          <div className="flex gap-2">
            <TransferForm accounts={accounts} onCreated={load} />
            <AccountForm onCreated={load} />
          </div>
        }
        testId="hesaplar-header"
      />

      {accounts.length === 0 ? (
        <EmptyState title="Henüz hesap yok" hint="İlk kasa veya banka hesabınızı ekleyin." testId="hesaplar-empty" />
      ) : (
        <>
          <Section title="Kasalar">
            {kasalar.length === 0 ? <div className="text-sm text-foreground/50">Kasa hesabı yok.</div> : <AccountGrid accounts={kasalar} onChanged={load} />}
          </Section>
          <Section title="Bankalar">
            {bankalar.length === 0 ? <div className="text-sm text-foreground/50">Banka hesabı yok.</div> : <AccountGrid accounts={bankalar} onChanged={load} />}
          </Section>
        </>
      )}
    </Layout>
  );
}
